/* eslint-disable flowtype/no-weak-types */
// @flow
import moment from 'moment';
import cookie from 'js-cookie';
import { get, padStart } from 'lodash';
import Uri from 'urijs';

import {
  COOKIE_WARNING_LOCALSTORAGE_KEY,
  COOKIES_ARRAY,
} from 'Components/widgets/cookie-warning/constants';
import { convertLocationToURI } from 'Utils/uri/uriUtils';
import config from 'Config/index';

type BuildProposalUrlOption = {
  +padCountryCode: boolean,
};

function formatDate(originalDateFormat, targetDateFormat) {
  return date => {
    return moment(date, originalDateFormat).format(targetDateFormat);
  };
}

function byIndex(prev: { index: number }, next: { index: number }): number {
  return prev.index - next.index;
}

function getBirthdaysToStringFormatter(birthdayDateFormat, targetDateFormat) {
  return birthdays => {
    const formattedBirthdays = birthdayDateFormat
      ? birthdays.map(formatDate(birthdayDateFormat, targetDateFormat))
      : birthdays;
    return formattedBirthdays.join(',');
  };
}

function param2query(item: { name: string, value: * }) {
  return [item.name, item.value].join('=');
}

function transport2string(item: string): ?string {
  return item === 'NO' ? undefined : item;
}

function unknownProperty<T>(item?: { value: T }): ?T {
  return item && item.value;
}

function transform(
  data: *,
  dateFormat: string,
  birthdayDateFormat: string,
  authenticate: boolean,
): ?* {
  function adultsAndChildren(adults: number): number {
    return adults + (data.children ? data.children : 0);
  }

  const conf = {
    pays: {
      name: 'PAYS',
      index: -2,
    },
    lang: {
      name: 'LANG',
      index: -1,
    },
    resortId: { name: 'village', index: 1 },
    duration: { name: 'dureeSejour', index: 2 },
    arrivalDate: {
      name: 'dateDeDebut',
      transform: formatDate('YYYYMMDD', dateFormat),
      index: 3,
    },
    transport: {
      name: 'villeDepart',
      transform: transport2string,
      index: 4,
    },
    adults: {
      name: 'nbParticipants',
      transform: adultsAndChildren,
      index: 5,
    },
    birthdays: {
      name: 'dateDeNaissance',
      transform: getBirthdaysToStringFormatter(birthdayDateFormat, dateFormat),
      index: 6,
    },
    children: { name: 'nbEnfants', index: 7 },
    visitorGUID: {
      name: 'visitorGUID',
      index: 8,
    },
    [COOKIE_WARNING_LOCALSTORAGE_KEY]: {
      name: COOKIE_WARNING_LOCALSTORAGE_KEY,
      index: 9,
    },
    ...COOKIES_ARRAY.reduce(
      (acc, key, i) => ({
        ...acc,
        // $FlowFixMe
        [key]: {
          name: key,
          index: 9 + i + 1,
        },
      }),
      {},
    ),
  };

  function entry2param(key): any {
    const myConf = conf[key];
    if (!myConf) {
      return undefined;
    }
    return {
      name: myConf.name,
      value: myConf.transform ? myConf.transform(data[key]) : data[key],
      index: myConf.index,
    };
  }

  return (
    Object.keys(data)
      .map(entry2param)
      .filter(unknownProperty)
      .sort(byIndex)
      .map(param2query)
      .join('&') + `&authenticate=${authenticate.toString()}`
  );
}

function buildUrl(formData: *, appContext: AppContext, birthdayDateFormat?: string = ''): string {
  const availableLocales = get(appContext, 'availableLocales');
  const host = config.get('booking.host');
  const endpoint = config.get('booking.endpoints.legacyProposals');

  const currentConfig = availableLocales[get(appContext, 'locale')];
  if (currentConfig && host) {
    formData.pays = currentConfig.PAYS;
    formData.lang = currentConfig.LANG;
    formData.visitorGUID = cookie.get('_ga');
    return new Uri(host)
      .path(endpoint)
      .query(
        transform(
          formData,
          currentConfig.BE.dateFormat,
          birthdayDateFormat,
          get(appContext, 'isUserLogged'),
        ),
      )
      .toString();
  }
  return '';
}

export const redirectToLegacy = (
  appContext: AppContext,
  productId: string,
  proposalId: string,
  queryData: * = {},
  hash: string = '',
  options: BuildProposalUrlOption = { padCountryCode: false },
) => {
  const host = config.get('booking.host');
  const endpoint = config.get('booking.endpoints.proposals');
  const currentConfig = get(appContext, 'availableLocales')[get(appContext, 'locale')];
  const paddedCountryCode = get(options, 'padCountryCode')
    ? padStart(currentConfig.PAYS, 3, '0')
    : currentConfig.PAYS;
  const groomedProductId = (productId || '').replace('_WINTER', '').replace('_SUMMER', '');

  if (host && endpoint && currentConfig) {
    return new Uri(host)
      .segment([endpoint, paddedCountryCode, currentConfig.LANG, groomedProductId, proposalId])
      .hash(hash)
      .query(queryData)
      .toString();
  }
};

function buildProposalUrl({
  appContext,
  productId,
  proposalId,
  queryData = {},
  hash = '',
  options = { padCountryCode: false },
  location,
}: {
  appContext: AppContext,
  productId: string,
  proposalId: string,
  queryData?: *,
  hash?: string,
  options?: BuildProposalUrlOption,
  location: RouterLocation<{}, *>,
}): ?string {
  if (location.queryParams.hasOwnProperty('legacy')) {
    return redirectToLegacy(appContext, productId, proposalId, queryData, hash, options);
  }
  const redirectionQueryParameters = {
    proposalIds: proposalId,
    selectedProposalId: proposalId,
    product: productId,
  };

  if (location.queryParams.locale) {
    // $FlowFixMe
    redirectionQueryParameters.locale = location.queryParams.locale;
  }

  // $FlowFixMe
  return convertLocationToURI(location)
    .segment([config.get('booking.endpoints.responsive')])
    .query(redirectionQueryParameters)
    .toString();
}

function buildUrlFromContextNumber(
  formData: *,
  appContext: AppContext,
  location: RouterLocation<{}, *>,
): ?string {
  const queryData = {
    UIDCM: get(appContext, 'neolaneId'),
    authenticate: appContext.isUserLogged,
  };
  return buildProposalUrl({
    appContext,
    productId: formData.resortId,
    proposalId: formData.reference,
    queryData,
    options: { padCountryCode: true },
    location,
  });
}

export { buildUrl, transform, buildProposalUrl, buildUrlFromContextNumber };
